import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { SettingsPage } from '../../contexts/settings-modal-context';
import { SettingsTabs } from '../../components/settings-tabs';
import { Header } from '../../components/layout/header';
import { Profile } from '../settings/profile';
import { Notifications } from '../settings/notifications';
import { InviteDependents } from '../settings/invite-dependents';
import { UnsavedChangesModal } from '../../components/modals/unsaved-changes-modal';
import useAuth from '../../hooks/utils/useAuth';
import { ROUTE_PATH } from '../../routes/route-paths';
import { tabParamToPage, pageToTabParam } from '../../utils/settings-utils';
import { openKeycloakChangePassword } from '../../utils/keycloak-utils';

const PROFILE_PAGE_MAP: Record<string, SettingsPage> = {
  'my profile': SettingsPage.myProfile,
  notifications: SettingsPage.notifications,
  'change password': SettingsPage.changePassword,
  'invite dependents': SettingsPage.inviteDependents,
};

export const ProfileSettingsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState<SettingsPage>(SettingsPage.myProfile);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false);
  const [nextPage, setNextPage] = useState<SettingsPage | undefined>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [profileSaveHandler, setProfileSaveHandler] = useState<(() => Promise<void>) | null>(null);

  // Parse tab from URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const page = tabParamToPage(tabParam, PROFILE_PAGE_MAP, SettingsPage.myProfile);

    // If Change Password is in URL, open Keycloak in new tab (fallback for direct navigation)
    if (page === SettingsPage.changePassword) {
      openKeycloakChangePassword();
      history.replace(`${ROUTE_PATH.SETTINGS_PROFILE}?tab=${pageToTabParam(SettingsPage.myProfile)}`);
      setCurrentPage(SettingsPage.myProfile);
    } else {
      setCurrentPage(page);
    }
  }, [location.search, history]);

  const navigateToPage = useCallback(
    (page: SettingsPage) => {
      setCurrentPage(page);
      history.replace(`${ROUTE_PATH.SETTINGS_PROFILE}?tab=${pageToTabParam(page)}`);
    },
    [history],
  );

  const handleSaveRequestReady = useCallback((handler: () => Promise<void>) => {
    if (typeof handler === 'function') {
      setProfileSaveHandler(() => handler);
    }
  }, []);

  const handleTabChange = useCallback(
    (page: SettingsPage) => {
      // Change Password opens in new tab, handled by SettingsTabs
      if (page === SettingsPage.changePassword) {
        return;
      }

      if (hasUnsavedChanges) {
        setShowUnsavedChangesModal(true);
        setNextPage(page);
      } else {
        navigateToPage(page);
      }
    },
    [hasUnsavedChanges, navigateToPage],
  );

  const handleUnsavedChangesWarning = useCallback((page: SettingsPage) => {
    setShowUnsavedChangesModal(true);
    setNextPage(page);
  }, []);

  const onCloseModalTab = useCallback(() => {
    setShowUnsavedChangesModal(false);
    setHasUnsavedChanges(false);
    if (nextPage) {
      navigateToPage(nextPage);
      setNextPage(undefined);
    }
  }, [nextPage, navigateToPage]);

  const onSaveFromModalSuccess = useCallback(() => {
    setShowUnsavedChangesModal(false);
    setHasUnsavedChanges(false);
    if (nextPage) {
      navigateToPage(nextPage);
      setNextPage(undefined);
    }
  }, [nextPage, navigateToPage]);

  const tabs = useMemo(() => {
    const baseTabs = [
      { page: SettingsPage.myProfile, label: 'My Profile' },
      { page: SettingsPage.notifications, label: 'Notifications' },
    ];

    if (user?.caller_role?.active_subscription?.package?.dependents_configuration?.enabled && !user.is_dependent) {
      baseTabs.push({ page: SettingsPage.inviteDependents, label: 'Invite Dependents' });
    }

    baseTabs.push({ page: SettingsPage.changePassword, label: 'Change Password' });

    return baseTabs;
  }, [user]);

  const renderPage = () => {
    switch (currentPage) {
      case SettingsPage.myProfile:
        return (
          <Profile
            showUnsavedChangesModal={showUnsavedChangesModal}
            onChange={() => setHasUnsavedChanges(true)}
            onCloseUnsavedChangesModal={() => setShowUnsavedChangesModal(false)}
            onDiscardUnsavedChangesModal={onCloseModalTab}
            onSuccessSubmitted={undefined}
            onSubmittingChange={setIsSubmitting}
            onSaveRequestReady={handleSaveRequestReady}
            onSaveSuccess={() => setHasUnsavedChanges(false)}
          />
        );
      case SettingsPage.notifications:
        return <Notifications onChange={() => setHasUnsavedChanges(true)} onSubmittingChange={setIsSubmitting} />;
      case SettingsPage.inviteDependents:
        return <InviteDependents />;
      case SettingsPage.changePassword:
        // Change Password opens in new tab, shouldn't reach here
        // Fallback to My Profile if somehow reached
        return <Profile onChange={() => setHasUnsavedChanges(true)} onSubmittingChange={setIsSubmitting} />;
      default:
        return <Profile onChange={() => setHasUnsavedChanges(true)} onSubmittingChange={setIsSubmitting} />;
    }
  };

  return (
    <>
      <Header />
      <div className="bg-whiteSmoke flex min-h-screen flex-col pt-[76px]">
        {/* Tabs */}
        <SettingsTabs
          tabs={tabs}
          activeTab={currentPage}
          basePath={ROUTE_PATH.SETTINGS_PROFILE}
          onTabChange={handleTabChange}
          hasUnsavedChanges={hasUnsavedChanges}
          onUnsavedChangesWarning={handleUnsavedChangesWarning}
        />

        <div className="flex-1 overflow-auto">{renderPage()}</div>

        <UnsavedChangesModal
          open={showUnsavedChangesModal}
          onClose={() => {
            setShowUnsavedChangesModal(false);
          }}
          onDiscard={onCloseModalTab}
          onSave={async () => {
            if (profileSaveHandler && typeof profileSaveHandler === 'function') {
              try {
                await profileSaveHandler();
                onSaveFromModalSuccess();
              } catch (error) {
                // Error handling - the handler should handle its own errors
              }
            }
          }}
          savingInProgress={isSubmitting}
        />
      </div>
    </>
  );
};
